import React, {useContext, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import Popup from "../../../../../components/WMS/Mobile/Popup";
import { ReactComponent as Scale } from "assets/img/wms/scale.svg"
import {Grid} from "@mui/material";
import CustomButton from "../../../../../components/CustomButton";
import {FormattedMessage} from "react-intl";
import TextEmphasisBloc from "../../../../../components/WMS/TextEmphasisBloc";
import PrintNode from "../../../../../printNode-js/printnode-ws-http-client";
import {WMSContext} from "../../../context/WMSContext";
import {ShipmentMessageSeverity} from "../../../../../utils/ShipmentMessageType";
import {subscribeToWaybillPrint} from "../../utils/waybillPrintSubscription";
import {red500} from "../../../../../assets/jss/main";
import {Link} from "react-router-dom";
import {getShipmentById} from "../../../../../api/shipment/getWithId";
import Badge from "../../../../../components/Badge";
import {PackingContext} from "../PackingOrderList/PackingContext";
import MultiExpForceStopModal from "./MultiExpForceStopModal";
import CustomTrackingNumberModal from "./CustomTrackingNumberModal";

WeighShipmentModal.propTypes = {
    open: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool,
    setIsLoading: PropTypes.func.isRequired,
    handleClose: PropTypes.func.isRequired,
    handleValidation: PropTypes.func.isRequired,
    shipmentWeigh: PropTypes.number.isRequired,
    setShipmentWeigh: PropTypes.func.isRequired,
    packingStationComputerId: PropTypes.number.isRequired,
    handleToast: PropTypes.func.isRequired,
    packingStep: PropTypes.object.isRequired,
    validPrint: PropTypes.func.isRequired
};

function WeighShipmentModal({open, handleClose, handleValidation, shipmentWeigh = 0, setShipmentWeigh, packingStationComputerId, isLoading = false, setIsLoading, handleToast, packingStep, validPrint}) {
    const {currentOrganization} = useContext(WMSContext)
    const {isMultiSessionPacking, nextShipment} = useContext(PackingContext)

    const shipmentWeighTimeout = useRef()
    const slipIsPrintedTimeout = useRef()

    const [printNodeWs, setPrintNodeWs] = useState()
    const [errorMessage, setErrorMessage] = useState()
    const [defaultShipmentWeight, setDefaultShipmentWeight] = useState(0)
    const [openCustomTrackingNumberModal, setOpenCustomTrackingNumberModal] = useState(false);

    const [stopMultiExpSession, setStopMultiExpSession] = useState(false)

    const printValidated = useRef(false)
    const handleShipmentMessageRef = useRef()
    handleShipmentMessageRef.current = handleShipmentMessage

    useEffect(() => {
        const unsubscribe = subscribeToWaybillPrint(
            packingStep.selectedShipment.shipmentId,
            (data) => handleShipmentMessageRef.current(data)
        )

        return () => {
            unsubscribe()
            clearTimeout(slipIsPrintedTimeout.current)
        }
    }, []);

    function handleShipmentWeigh(tempShipmentWeigh) {
        if (shipmentWeigh !== tempShipmentWeigh) {
            clearTimeout(shipmentWeighTimeout.current)
        }
        shipmentWeighTimeout.current = setTimeout(() => {
            // Convert from g to kg
            tempShipmentWeigh = tempShipmentWeigh/1000
            setShipmentWeigh(tempShipmentWeigh)
        }, 2000)
    }

    const handleEdit = (value) => {
        setShipmentWeigh(value)
    }

    function printNodeAuthenticate (authData)  {
        if (authData.error) {
            handleToast('error')
            return
        }

        var computer = this.getComputerConnections({computerId: packingStationComputerId}, function callBack(computers) {
        })

        var scales = this.getScales({computerId: packingStationComputerId}, function callBack(scalesMeasurement) {
            handleShipmentWeigh(scalesMeasurement.getKilogramMeasurementFromGram().g)
        });
    }

    function printNodeError (err){
        setErrorMessage(err)
    }

    // The API sends one "printed" message per document (waybill, return waybill, return
    // form): only the first one moves the packing forward.
    function handlePrintValidated() {
        if (printValidated.current) {
            return
        }
        printValidated.current = true
        validPrint()
    }

    function handleShipmentMessage (data) {
        clearTimeout(slipIsPrintedTimeout.current)
        if (printValidated.current) {
            return
        }
        if (ShipmentMessageSeverity.ERROR === data.severity) {
            setIsLoading(false)
            setErrorMessage(data.message)
        }
        if (ShipmentMessageSeverity.SUCCESS === data.severity) {
            handlePrintValidated()
        }
    }

    function handleValid () {
        handleValidation(false, getShipmentWeight())
        slipIsPrintedTimeout.current = setTimeout(() => {
            getShipmentById(packingStep.selectedShipment.shipmentId).then((response) => {
                if (response.documents && response.trackingNumber) {
                    handlePrintValidated()
                } else {
                    setIsLoading(false)
                    setErrorMessage(response.error ?? <FormattedMessage id={"wms.packing.modal.shipmentWeigh.defaultErrorMessage"}/>)
                }
            })
        }, 5000)
    }

    function getShipmentWeight() {
        let numericValue = shipmentWeigh;

        if (typeof shipmentWeigh === 'string') {
            numericValue = parseFloat(shipmentWeigh.replace(',', '.'))
        }

        if (!isNaN(numericValue) && numericValue > 0) {
            return numericValue
        }
        return defaultShipmentWeight
    }

    useEffect(() => {
        if(shipmentWeigh && shipmentWeigh > 0 && printNodeWs) {
            printNodeWs.closeSocket()
        }
    }, [shipmentWeigh]);

    useEffect(() => {
        if (open && currentOrganization) {
            const printNodeWebSocket = new PrintNode.WebSocket({apiKey: currentOrganization.printNodeKey}, printNodeAuthenticate, printNodeError)
            setPrintNodeWs(printNodeWebSocket)
        }
    }, [open]);

    useEffect(() => {
        if (currentOrganization?.wmsDefaultWeight) {
            let shipmentTotalWeigh = 0

            packingStep.selectedShipment.lineItems.map((item) => {
                if (item.isPacked) {
                    shipmentTotalWeigh += item.weight ?? 0
                }
            })

            setDefaultShipmentWeight(parseFloat(shipmentTotalWeigh.toFixed(3)))
        }
    }, [currentOrganization?.wmsDefaultWeight]);
  
    if (stopMultiExpSession) {
        return (
            <MultiExpForceStopModal open={stopMultiExpSession} handleClose={() => setStopMultiExpSession(false)}/>
        )
    }

    return (
        <Popup
            open={open}
            onClose={isMultiSessionPacking ? () => setStopMultiExpSession(true) : handleClose}
            icon={<Scale/>}
            title={<FormattedMessage id={"wms.packing.modal.shipmentWeigh.title"}/>}
            subtitle={<FormattedMessage id={"wms.packing.modal.shipmentWeigh.subtitle"}/>}
            strictOnclose
        >

            {openCustomTrackingNumberModal && (
                <CustomTrackingNumberModal handleValidation={handleValid} setOpenCustomTrackingNumberModal={setOpenCustomTrackingNumberModal} />
            )}

            <Grid container direction={"column"} gap={"16px"}>
                <TextEmphasisBloc
                    title={<FormattedMessage id={"wms.packing.modal.shipmentWeigh.label"}/>}
                    leftPart={shipmentWeigh !== "" ? (shipmentWeigh || defaultShipmentWeight) : ""}
                    rightPart={"kg"}
                    placeholder={<FormattedMessage id={"wms.packing.modal.shipmentWeigh.placeholder"}/>}
                    isEditable={true}
                    editLeftPart={handleEdit}
                />

                {errorMessage && (
                    <Grid container direction={"column"} gap={"8px"}>
                        <Grid container direction={"column"}>
                            <Grid item><FormattedMessage id={"wms.packing.modal.shipmentWeigh.error"}/></Grid>
                            <Grid item sx={{color: red500}}>{errorMessage}</Grid>
                        </Grid>
                        <Grid container direction={"column"}>
                            <Grid item><FormattedMessage id={"wms.packing.modal.shipmentWeigh.toOMS"}/></Grid>
                            <Grid item>
                                <Link target="_blank" to={"../picking/orders/show/"+packingStep.selectedShipment.orderId}><FormattedMessage id={"wms.packing.modal.shipmentWeigh.toOMS.link"}/></Link>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <CustomButton
                                loading={isLoading}
                                disabled={isLoading}
                                onClick={() => handleValid()}
                                fullWidth
                                color={"default"}
                                style={{marginTop: "8px"}}
                            >
                                {!isLoading ?
                                    <FormattedMessage id={"wms.packing.modal.scanDropZone.printButton"}/> :
                                    <FormattedMessage id={"wms.packing.modal.scanDropZone.printButton.isLoading"}/>
                                }

                            </CustomButton>
                        </Grid>
                    </Grid>
                )}

                {getShipmentWeight() > 0 && !errorMessage && (
                    <Grid item>
                        <CustomButton
                            loading={isLoading}
                            disabled={isLoading}
                            onClick={() => handleValid()}
                            fullWidth
                        >
                            {!isLoading ?
                                <FormattedMessage id={"wms.packing.modal.shipmentWeigh.validButton"}/> :
                                <FormattedMessage id={"wms.packing.modal.shipmentWeigh.validButton.isLoading"}/>
                            }
                        </CustomButton>
                    </Grid>
                )}

                <CustomButton
                    fullWidth
                    onClick={() => setOpenCustomTrackingNumberModal(true)}
                    loading={isLoading}
                    disabled={isLoading}
                >
                    <FormattedMessage id={"wms.packing.modal.shipmentWeigh.customTrackingNumberDrawer.button"}/>
                </CustomButton>

                {isMultiSessionPacking && (
                    <Grid
                        container
                        direction={"column"}
                        alignItems={'center'}
                        gap={"8px"}
                    >
                        <CustomButton
                            fullWidth
                            onClick={() => nextShipment()}
                            loading={isLoading}
                            disabled={isLoading}
                            color={'lightRed'}
                            variant={'nude'}
                        >
                            <FormattedMessage id={"wms.packing.item.multiExpSession.nextShipment"}/>
                        </CustomButton>

                        <Badge
                            size={"large"}
                            rounded
                            label={<FormattedMessage id={"wms.packing.item.multiSessionPacking.notice"}/>}
                            color={"online"}
                        />
                    </Grid>
                )}
            </Grid>
        </Popup>
    );
}

export default WeighShipmentModal;
